import { Box, List, darken } from '@mui/material'
import { ArrowRightGrey } from 'assets/img'
import { ReactComponent as ArrowDown } from 'assets/img/arrow-down.svg'
import BlockOverlay, { BlockOverlayProps } from 'components/BlockOverlay'
import { GroupCardAvatar } from 'components/cards/styled'
import CalendarSelectPopup, {
  CalendarSectionItem
} from 'components/popups/CalendarSelectPopup'
import { SelectableItem } from 'components/popups/SelectItemWithSearchPopup'
import { ContentProps, prepareCalendars, prepareSections } from './EventPopup'
import PopupSection from 'components/popup/PopupSection'
import PopupSectionRow from 'components/popup/PopupSectionRow'
import { useCalendarSync } from 'components/providers/CalendarSyncProdivder'
import { useNotification } from 'components/providers/NotificationProvider'
import { IEventDetails } from 'hooks/useEventDetails'
import { t } from 'i18next'
import { GroupPrivacy } from 'model/models/Group'
import { useEffect, useState } from 'react'
import { Controller } from 'react-hook-form'
import Database from 'services/db/Database'
import { localEventByLocalId } from 'services/db/Queries/Event'
import { localGroupById } from 'services/db/Queries/GroupQueries'
import LocalStorage from 'services/LocalStorage'
import Logger from 'services/Logger'
import { theme } from 'theme'
import { DocType, EventDoc, TaskType, UserSettingsDoc } from 'types/api/changes'
import { ICalendar } from 'types/google_events'
import { Model } from 'types/model'
import { LocalStorageKey } from 'types/services/localStorage'
import {
  defaultConvertedCalendarPhoto,
  defaultGroupImage
} from 'utils/api/groups'
import { is24meApp } from 'utils/appType'
import { APP_COLOR } from 'utils/colors'
import { CurrentConvertedCalendars } from 'utils/google_events'
import { isConvertedGroup, isConvertedGroupId } from 'utils/groups'
import { removePrefix } from 'utils/object'

import groupDefaultPhoto from '../../../assets/icons/group-default.svg'
import channelDefaultPhoto from '../../../assets/icons/channel_calendar.svg'
import i18n from 'i18n'

export interface CalendarSectionProps extends ContentProps, BlockOverlayProps {
  userSettings: Model<UserSettingsDoc> | UserSettingsDoc
  setGroupID: (id: string) => void
  setThirdPartyId: (id: string) => void
  thirdParty: string
  selectedGroupId?: string
  setType: (type: DocType) => void
  eventHook: IEventDetails
  SupplementaryGroupsIDs?: string[]
  setRegularEventColor?: (color: string) => void
}

export default function CalendarSection(props: CalendarSectionProps) {
  const calendarSync = useCalendarSync()
  const { showNotification } = useNotification()
  const currentGroupIsConvertedCalendar =
    isConvertedGroup(props.group) ||
    props.event.TaskType === TaskType.personalEvent ||
    props.eventHook.getValues('TaskType') === TaskType.personalEvent

  const groupOrCalendarId = props.selectedGroupId

  const [calendarTitle, onCalendarTitle] = useState<string>(
    currentGroupIsConvertedCalendar
      ? calendarSync.calendarTitle(props.thirdParty, props.group)
      : (props.group?.Name ?? '')
  )

  const [isExpanded, setIsExpanded] = useState(false)

  useEffect(() => {
    Logger.blue(
      'EventPOpup:CalendarSection:useEffect',
      props.thirdParty,
      props.selectedGroupId
    )
    if (is24meApp()) {
      localGroupById(props.eventHook.getValues('GroupID') ?? '').then(
        (localGroup) => {
          onCalendarTitle(
            currentGroupIsConvertedCalendar
              ? calendarSync.calendarTitle(props.thirdParty, localGroup)
              : (props.group?.Name ?? '')
          )
        }
      )
    }
  }, [props.thirdParty, props.selectedGroupId])

  const [showCalendarPicker, onShowCalendarPicker] = useState(false)

  const onCalendarPick = (calendar: SelectableItem) => {
    if (props.event._id?.length !== 0) {
      showNotification(t('changeNotSupported'))
      return
    }
    onShowCalendarPicker(false)
    Logger.orange('calendar', calendar)
    const isSharedCalendar =
      (calendar as ICalendar)?.primaryCalendarEmail !== undefined
    const thirdPartyCalendar = CurrentConvertedCalendars.find(
      (userCalendar) => {
        return userCalendar.id === removePrefix(calendar.id ?? '')
      }
    )
    Logger.orange('thirdPartyCalendar', thirdPartyCalendar)
    const newGroupId = isSharedCalendar
      ? (calendar.localId ?? calendar.id)
      : calendar.id
    Logger.orange('calendar', newGroupId)
    Database.write(async () => {
      if (props.event.syncStatus === 'deleted') return
      ;(await localEventByLocalId(props.event.id ?? ''))?.update(
        (event: EventDoc) => {
          Logger.blue('Event:UpdateColor', thirdPartyCalendar)
          if (thirdPartyCalendar)
            event.Color =
              darken(thirdPartyCalendar?.backgroundColor ?? '', 0.15) ??
              props.event.Color

          event.ThirdPartyID = `${calendar.id}`
          event.PersonalOwnerId = thirdPartyCalendar
            ? `${(calendar as ICalendar).primaryCalendarEmail}`
            : (LocalStorage.get(LocalStorageKey.USER_ID) ?? '')
        }
      )
    }).then(() => {
      onCalendarTitle(calendar.title)
      if (isSharedCalendar) {
        props.eventHook.setTaskType(TaskType.personalEvent)
      } else {
        props.eventHook.setTaskType(TaskType.meet)
      }
      props.eventHook.setOwnerId(
        thirdPartyCalendar
          ? `${(calendar as ICalendar).primaryCalendarEmail}`
          : (LocalStorage.get(LocalStorageKey.USER_ID) ?? '')
      )
      props.setThirdPartyId(`${calendar.id}`)
      localGroupById(newGroupId ?? '').then((localGroup) => {
        if (localGroup?.id) props.setGroupID(localGroup._id)
      })
    })
  }

  const showCalendarPickerForGoogleEvent = () => {
    onShowCalendarPicker(true)
  }

  const [calendars, onCalendars] = useState<SelectableItem[]>([])

  //load groups for the list
  useEffect(() => {
    prepareCalendars(props.account, groupOrCalendarId).then((data) => {
      onCalendars(data)
    })
  }, [props.event, props.thirdParty])

  const sections: CalendarSectionItem[] = prepareSections(
    calendars,
    true,
    groupOrCalendarId,
    props.SupplementaryGroupsIDs
  )

  Logger.blue('CalendarSection', groupOrCalendarId)

  const fallbackCalendar = !calendarTitle
    ? calendars.find(
        (c) => c.title && c.id && props.SupplementaryGroupsIDs?.includes(c.id)
      )
    : undefined

  const effectiveTitle = calendarTitle || fallbackCalendar?.title
  const effectiveId = calendarTitle ? groupOrCalendarId : fallbackCalendar?.id
  const effectiveGroup =
    effectiveId === groupOrCalendarId
      ? props.group
      : calendars.find((c) => c.id === effectiveId)?.group

  return (
    <CalendarSelectPopup
      group={props.group}
      account={props.account}
      eventHook={props.eventHook}
      onCalendarPick={onCalendarPick}
      calendars={sections}
      open={showCalendarPicker}
      setOpen={onShowCalendarPicker}
      SupplementaryGroupsIDs={props.SupplementaryGroupsIDs ?? []}
    >
      <div>
        <PopupSection
          isUiBlocked={props.isUiBlocked}
          blockedOverlayClick={props.blockedOverlayClick}
          title={`${t('calendar')}`}
        >
          <List
            style={{
              paddingBottom: '16px',
              borderBottom: '1px solid',
              borderColor: theme.palette.border.primary
            }}
          >
            {effectiveTitle && effectiveId && (
              <PopupSectionRow
                key={effectiveId}
                iconClass="icon-big"
                icon={
                  <GroupCardAvatar
                    asGroup
                    img={
                      calendars.find((value) => value.id === effectiveId)?.photo
                    }
                    name=""
                    size={24}
                    emptyPic={
                      isConvertedGroupId(effectiveId)
                        ? defaultConvertedCalendarPhoto(effectiveId)
                        : effectiveGroup?.PrivacyMode === GroupPrivacy.CHANNEL
                          ? (channelDefaultPhoto as any)
                          : (groupDefaultPhoto as any)
                    }
                  />
                }
                title={
                  (props.SupplementaryGroupsIDs?.length ?? 0) -
                    (effectiveId !== groupOrCalendarId ? 1 : 0) >
                  0
                    ? t('primaryLabel', { calendar: effectiveTitle })
                    : effectiveTitle
                }
                action={showCalendarPickerForGoogleEvent}
                spacing={theme.spacing(0)}
                actionId="change-calendar-button"
              />
            )}
            <Box height={4} />
            <Controller
              name="SupplementaryGroupsIDs"
              control={props.eventHook.control}
              defaultValue={props.event.SupplementaryGroupsIDs}
              render={({ field }) => {
                if (!field.value || field.value.length === 0) return <></>

                Logger.orange('SupplementaryGroupsIDs', field.value, isExpanded)

                // filter groups which are existing in calendars
                const existingGroupIds = field.value
                  .filter((groupId) =>
                    calendars.find((value) => value.id === groupId)
                  )
                  .filter((groupId) => groupId !== effectiveId)

                if (existingGroupIds.length === 0) return <></>

                if (!isExpanded) {
                  return (
                    <PopupSectionRow
                      iconClass="icon-big"
                      icon={<Box width={24} height={24} />}
                      title={
                        <Box display="flex" alignItems="center" gap={2}>
                          <span>{`+${existingGroupIds.length} Calendars`}</span>
                          <ArrowDown width={10} height={10} />
                        </Box>
                      }
                      action={() => setIsExpanded(true)}
                      cursor="pointer"
                    />
                  )
                }

                return (
                  <Box>
                    {field.value?.map((groupId) => {
                      const group = calendars.find(
                        (value) => value.id === groupId
                      )?.group
                      if (!group) return <></>
                      return (
                        <PopupSectionRow
                          key={groupId}
                          title={
                            calendars.find((value) => value.id === groupId)
                              ?.title
                          }
                          action={showCalendarPickerForGoogleEvent}
                          iconClass="icon-big"
                          icon={
                            <GroupCardAvatar
                              asGroup
                              img={
                                calendars.find((value) => value.id === groupId)
                                  ?.photo
                              }
                              name=""
                              size={24}
                              emptyPic={
                                isConvertedGroup(group)
                                  ? defaultGroupImage(group)
                                  : group.PrivacyMode === GroupPrivacy.CHANNEL
                                    ? channelDefaultPhoto
                                    : groupDefaultPhoto
                              }
                            />
                          }
                        />
                      )
                    })}
                  </Box>
                )
              }}
            />
          </List>
          <ArrowRightGrey
            style={{
              transform: i18n.dir() === 'ltr' ? '' : 'rotate(180deg)',
              top: '46%',
              right: i18n.dir() === 'ltr' ? 24 : undefined,
              left: i18n.dir() === 'ltr' ? undefined : 24,

              position: 'absolute',
              cursor: 'pointer'
            }}
            onClick={showCalendarPickerForGoogleEvent}
            width={16}
            height={16}
          />
        </PopupSection>
      </div>
    </CalendarSelectPopup>
  )
}
